import { trackFullStoryEvent } from 'helpers/fullstory';

import type { StageCardDisplayMode } from 'containers/user/critical-elements-monitoring/components/funnel-stage-card';
import type {
  CompareTo,
  CriticalElementSummary,
  ExtendWithOption,
  MonitoringShowToggles,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

import {
  MONITORING_EVENT,
  type MonitoringComparison,
  type MonitoringDisplayModeSection,
  type MonitoringExpansionAction,
  type MonitoringSelectionAction,
} from './monitoring-analytics.constants';

const COMPARISON_BY_SHOW_TOGGLE: Record<
  keyof MonitoringShowToggles,
  MonitoringComparison
> = {
  client: 'mySchool',
  cohort: 'cohort',
  vsCohort: 'mySchoolVsCohort',
};

interface TrackMonitoringExtendWithToggledArgs {
  option: ExtendWithOption;
  action: MonitoringSelectionAction;
}

export const trackMonitoringExtendWithToggled = ({
  option,
  action,
}: TrackMonitoringExtendWithToggledArgs): void => {
  trackFullStoryEvent(MONITORING_EVENT.extendWithToggled, { option, action });
};

type TrackMonitoringDisplayOptionChangedArgs =
  | { displayOption: 'enteringClass'; value: number }
  | { displayOption: 'changeInTime'; value: CompareTo }
  | { displayOption: 'cohort'; value: string };

export const trackMonitoringDisplayOptionChanged = ({
  displayOption,
  value,
}: TrackMonitoringDisplayOptionChangedArgs): void => {
  trackFullStoryEvent(MONITORING_EVENT.displayOptionChanged, {
    displayOption,
    value: String(value),
  });
};

interface TrackMonitoringCompareToToggledArgs {
  showToggle: keyof MonitoringShowToggles;
  action: MonitoringSelectionAction;
}

export const trackMonitoringCompareToToggled = ({
  showToggle,
  action,
}: TrackMonitoringCompareToToggledArgs): void => {
  trackFullStoryEvent(MONITORING_EVENT.compareToToggled, {
    comparison: COMPARISON_BY_SHOW_TOGGLE[showToggle],
    action,
  });
};

interface TrackMonitoringDisplayModeChangedArgs {
  section: MonitoringDisplayModeSection;
  displayMode: StageCardDisplayMode;
}

export const trackMonitoringDisplayModeChanged = ({
  section,
  displayMode,
}: TrackMonitoringDisplayModeChangedArgs): void => {
  trackFullStoryEvent(MONITORING_EVENT.displayModeChanged, {
    section,
    displayMode,
  });
};

interface TrackMonitoringCriticalElementToggledArgs {
  criticalElement: Pick<CriticalElementSummary, 'id' | 'name'>;
  action: MonitoringExpansionAction;
}

export const trackMonitoringCriticalElementToggled = ({
  criticalElement,
  action,
}: TrackMonitoringCriticalElementToggledArgs): void => {
  trackFullStoryEvent(MONITORING_EVENT.criticalElementToggled, {
    criticalElementId: criticalElement.id,
    criticalElementName: criticalElement.name,
    action,
  });
};
